import {
  resolveState,
  type StateDomainKey,
} from '../../utility/state-colours/state-colour-registry';

/**
 * The audit-trail data layer: the shape every trail arrives in, and the grouping that turns
 * database rows back into the events a person performed.
 *
 * WHY THIS EXISTS AS ITS OWN MODULE. The server writes one row per changed field, so a save that
 * touched six fields is six rows sharing one instant. A reader does not care how many columns an
 * edit touched - they want to know how many times the record was edited and what each edit did. The
 * regrouping is therefore a real transformation with real edge cases (an auditor note must never
 * merge with a system-recorded change; a repeated note must not print six times), and it belongs
 * where it can be tested directly rather than inside a render method.
 */

/**
 * The members every audit DTO in the product carries, mirroring the server's `IAuditEntryDto`.
 *
 * Structural on purpose: each trail's generated DTO satisfies this without declaring it, so hosts
 * pass their DTOs straight in. Before #334 every host flattened its own DTO into a two-string
 * shape, and most of them dropped the field diff on the way - the private mapper was the defect,
 * so the fix is to have no mapper at all.
 *
 * Everything past the first three is optional because two trails legitimately lack members: GDC
 * surfaces its entity rather than a DTO and has no auditor-note concept, and workflow transitions
 * carry no field diff.
 */
export interface AuditEntryLike {
  id: string;
  createdUtc: string;
  loginUserId: string;
  /** The module's own action token. Each trail has its own vocabulary; see `formatAuditActionLabel`. */
  action?: string | undefined;
  /**
   * The state the record moved between, where the event moved it.
   *
   * Absent from the server contract on purpose - a report's states and a certificate's states are
   * different vocabularies and sharing an enum would invent a commonality that is not there. They
   * are still both strings by the time they reach a screen, so the trail can render a transition
   * without a host knowing anything about how it is rendered.
   */
  fromState?: string | undefined;
  toState?: string | undefined;
  userDisplayName?: string | undefined;
  notes?: string | undefined;
  fieldName?: string | undefined;
  oldValue?: string | undefined;
  newValue?: string | undefined;
  isAuditorNote?: boolean | undefined;
}

/**
 * One field's before and after, with the label a reader knows the field by. The values are the
 * host's words where it supplied a `valueLabel` (a tick reads Yes / No), and the stored text
 * otherwise; this is what every surface of the trail renders, never the raw row.
 */
export interface AuditFieldChange {
  fieldName: string;
  label: string;
  oldValue?: string | undefined;
  newValue?: string | undefined;
  /**
   * Set where the host declared these values server-written kept text (AuditKeptTextRule), so their
   * tags are resolved; otherwise they are drawn exactly as stored (U50).
   */
  keptText?: boolean | undefined;
  /**
   * Set where the host declared the field a SET recorded one member per row (AuditGroupingOptions
   * memberField): the value arriving is a member added and the value leaving one removed, never a
   * single value set or cleared (SET-LOOKUP-008).
   */
  member?: boolean | undefined;
}

/** Which part of an audit row a kept-text rule is asked about: its note, or its recorded values. */
export type AuditTextPart = 'note' | 'value';

/**
 * Whether a row's note or values are text the SERVER wrote, carrying tags to resolve at render time
 * (terms, and a person by reference with their card, CONTACT-010), rather than text a person typed.
 *
 * WHY THE HOST DECLARES IT (U50, the part 4 final review F3). A person's own note or value can hold
 * "{{...}}" as ordinary text. Resolved, it showed a MISSING TERM marker, and a typed
 * "{{person <someone's id>}}" drew as that person with their card, so anyone who could add a note
 * could put words in another person's name. Nothing in the text says who wrote it; the host knows
 * which of its actions the server composes, so it names them, and everything else is drawn raw, as
 * the trail drew it before U48.
 */
export type AuditKeptTextRule<T extends AuditEntryLike = AuditEntryLike> = (
  entry: T,
  part: AuditTextPart
) => boolean;

/**
 * One event: everything written by a single save, by one person, under one action.
 */
export interface AuditEventGroup<T extends AuditEntryLike> {
  /** Identity for the table's row key and expansion state. Stable across re-renders of the same data. */
  key: string;
  createdUtc: string;
  loginUserId: string;
  actionLabel: string;
  isAuditorNote: boolean;
  /** Field diffs recorded by this save, in the order the server wrote them. */
  changes: AuditFieldChange[];
  /** Human-written notes on this event, de-duplicated - the same note may be copied onto every diff row. */
  notes: string[];
  /** The notes above that the host's kept-text rule declared server-written; the rest draw raw (U50). */
  keptNotes: string[];
  /**
   * A value recorded without a field name, which several actions legitimately do: a product's
   * creation row carries the identifier, a WERS link's row carries the link. Kept so the trail can
   * still say what was recorded rather than showing an empty line where the old per-page mappers
   * printed the value.
   */
  unnamedValue?: string;
  /** Whether `unnamedValue` is server-written kept text, by the host's rule; raw otherwise (U50). */
  unnamedValueKept?: boolean;
  /** The rows this group was built from, for hosts that need the original DTO. */
  entries: T[];
}

/** How a host names its own action tokens, fields and values. */
export interface AuditGroupingOptions<T extends AuditEntryLike> {
  actionLabel?: (entry: T) => string;
  fieldLabel?: (fieldName: string) => string;
  /**
   * Names a recorded value as the host's reader knows it (D-134): a tick the server stored as
   * "true" reads "Yes". A host hook rather than a trail rule because only the host knows which of
   * its fields are ticks - a text field may legitimately hold the word "true". Called only for a
   * side that holds a value, with that value trimmed, so an absent side stays absent.
   */
  valueLabel?: (fieldName: string, value: string) => string;
  /** Which rows' notes and values are server-written kept text; absent, none are (U50). */
  keptText?: AuditKeptTextRule<T>;
  /**
   * Which fields are a SET the server records one member per row, the member's value arriving when
   * it was added and leaving when it was removed: an operating type's accepted deflection ratios
   * (SET-LOOKUP-008). A host hook, as the value names are, because only the host knows which of its
   * fields are sets; such a change reads "added" / "removed" rather than "set to" / "cleared".
   */
  memberField?: (fieldName: string) => boolean;
}

/**
 * Regroups audit rows into the events that produced them, newest first.
 *
 * The grouping key is the saved instant, which is only trustworthy because the database stamps one
 * value per `SaveChanges` call (`AGWADatabase.StampTimestamps`). It is deliberately not a tolerance
 * window: rows written by separate saves have been observed one microsecond apart, so any "close
 * enough" heuristic would silently merge two distinct edits.
 *
 * The action and the actor join the key so that a save which both changed fields and moved the
 * record's state reads as the two things it was. `isAuditorNote` joins it so an auditor's private
 * note can never be absorbed into a row of system-recorded changes, which would both misattribute
 * it and put it behind the wrong visibility gate.
 */
export function groupAuditEntries<T extends AuditEntryLike>(
  entries: readonly T[],
  options: AuditGroupingOptions<T> = {}
): AuditEventGroup<T>[] {
  const actionLabel =
    options.actionLabel ?? ((entry: T) => formatAuditActionLabel(entry.action));
  const fieldLabel = options.fieldLabel ?? formatAuditFieldLabel;
  const { valueLabel } = options;
  // Default false: text is drawn as stored unless the host names it server-written (U50).
  const isKept = (entry: T, part: AuditTextPart): boolean =>
    options.keptText?.(entry, part) === true;
  // Mapped here, once, rather than where each surface renders: the collapsed summary and the
  // expanded detail both read the change built below, so they can never disagree about what a
  // value is called. A blank side is returned untouched, so describeValueChange and the detail's
  // "not set" / "cleared" still see it as absent rather than as a host word such as "No".
  const nameValue = (
    fieldName: string,
    value: string | undefined
  ): string | undefined => {
    const text = value?.trim();
    return text && valueLabel ? valueLabel(fieldName, text) : value;
  };

  const groups = new Map<string, AuditEventGroup<T>>();

  for (const entry of entries) {
    const label = actionLabel(entry);
    const isAuditorNote = entry.isAuditorNote === true;
    // The transition joins the key for the same reason the action does: one save that both edited
    // fields and moved the record should read as the two things it was, not as one ambiguous row.
    const key = `${entry.createdUtc}|${entry.loginUserId}|${label}|${isAuditorNote}|${entry.fromState ?? ''}>${entry.toState ?? ''}`;

    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        createdUtc: entry.createdUtc,
        loginUserId: entry.loginUserId,
        actionLabel: label,
        isAuditorNote,
        changes: [],
        notes: [],
        keptNotes: [],
        entries: [],
      };
      groups.set(key, group);
    }

    group.entries.push(entry);

    if (entry.fieldName) {
      group.changes.push({
        fieldName: entry.fieldName,
        label: fieldLabel(entry.fieldName),
        oldValue: nameValue(entry.fieldName, entry.oldValue),
        newValue: nameValue(entry.fieldName, entry.newValue),
        keptText: isKept(entry, 'value'),
        member: options.memberField?.(entry.fieldName) === true,
      });
    } else if (!group.unnamedValue) {
      // A row with values but no field name: the value is the whole record of what happened, so it
      // is kept rather than dropped for want of a label. First one wins - these actions write one.
      group.unnamedValue =
        entry.newValue?.trim() || entry.oldValue?.trim() || undefined;
      group.unnamedValueKept = !!group.unnamedValue && isKept(entry, 'value');
    }

    // The same note can be written onto every diff row of one save; a reader should see it once.
    const note = entry.notes?.trim();
    if (note && !group.notes.includes(note)) {
      group.notes.push(note);
      if (isKept(entry, 'note')) group.keptNotes.push(note);
    }
  }

  // Newest first. Hosts mostly send rows already ordered, but nothing in the contract promises it.
  return [...groups.values()].sort((a, b) =>
    b.createdUtc.localeCompare(a.createdUtc)
  );
}

/**
 * Whether this event has anything worth expanding to.
 *
 * Drives the presence of the expander control, not just its target: an entry with one change and no
 * note shows that change on its own row, so offering an expander would promise detail that does not
 * exist. Absent detail takes no space and grows no affordance.
 */
export function hasExpandableDetail<T extends AuditEntryLike>(
  group: AuditEventGroup<T>
): boolean {
  return group.changes.length > 1 || group.notes.length > 0;
}

/**
 * States a single field's change in the terms a reader thinks in.
 *
 * A value arriving where there was none is "set", not "changed from blank", and a value removed is
 * "cleared" rather than a change to an invisible empty string - both were called out as misreadings
 * of the trail in the acceptance criteria for story 1.
 */
export function describeValueChange(change: AuditFieldChange): string {
  const from = change.oldValue?.trim() ?? '';
  const to = change.newValue?.trim() ?? '';

  // A member of a set arrives and leaves; it is never a value set or cleared (SET-LOOKUP-008).
  if (change.member) {
    if (!from && to) return `added ${to}`;
    if (from && !to) return `removed ${from}`;
  }

  if (!from && to) return `set to ${to}`;
  if (from && !to) return `cleared (was ${from})`;
  if (from && to) return `${from} → ${to}`;

  // Both sides empty: the row records that the field was touched but holds no values to show.
  // Saying "changed" is the honest reading; inventing a before or after would not be.
  return 'changed';
}

/**
 * The one-line summary carried by the collapsed row.
 *
 * States the count first because story 2 asks the trail to say how many fields a save changed, then
 * names the first field: a bare count answers "how much" but never "was it the one I am looking
 * for", which is the question a reader scanning a trail is actually asking.
 */
export function summariseChanges(changes: readonly AuditFieldChange[]): string {
  if (changes.length === 0) return '';

  const first = changes[0]!;
  if (changes.length === 1) {
    return `${first.label}: ${describeValueChange(first)}`;
  }

  return `${changes.length} fields changed: ${first.label}, +${changes.length - 1} more`;
}

/**
 * Action tokens whose plain reading is wrong or awkward.
 *
 * Everything else is derived from the token itself, so a module adding an action gets a sensible
 * label with no edit here. Keys are lower-cased because the same concept arrives as `Create` from
 * GDC and `Created` from the product trail, and the server's casing is not a contract.
 */
const ACTION_LABEL_EXCEPTIONS: Readonly<Record<string, string>> = {
  none: 'Action',
  create: 'Created',
  submit: 'Submitted',
  approve: 'Approved',
  reject: 'Rejected',
  archive: 'Archived',
  update: 'Updated',
  softdelete: 'Soft deleted',
  noteappended: 'Note',
  note: 'Note',
  metadataupdate: 'Details updated',
  tagsupdate: 'Tags updated',
};

/**
 * Names an action token as a reader would say it.
 *
 * Replaces six near-identical private copies of this mapping across the report, certificate and GDC
 * pages. They were copies rather than a shared function because the DTOs disagreed, not because the
 * vocabularies did: nearly every branch in every copy said the same thing.
 *
 * The label always starts with a capital, whatever casing the token arrived in: the API
 * serialises enums camelCase, so the wire carries `approvedImmediately`, and a label that kept
 * the token's first letter read "approved immediately" beside "Created" (GA-CERT-022).
 */
export function formatAuditActionLabel(
  action: string | null | undefined
): string {
  const raw = (action ?? '').trim();
  if (!raw) return 'Action';

  const exception = ACTION_LABEL_EXCEPTIONS[raw.toLowerCase()];
  if (exception) return exception;

  return splitIdentifier(raw)
    .map((word, index) => {
      if (index === 0) return word.charAt(0).toUpperCase() + word.slice(1);
      // Only a plain capitalised word is safe to lower-case; anything else is an acronym such as
      // WERS or GDC, and folding it would produce "Wers link created".
      return /^[A-Z][a-z0-9]*$/.test(word) ? word.toLowerCase() : word;
    })
    .join(' ');
}

/**
 * The fallback name for a field the host has no label for.
 *
 * Deliberately weak: it makes a raw column name readable but cannot know units or domain wording.
 * The real labels live beside the field definitions in each module and reach the trail through the
 * `fieldLabel` property, because "Positive air at 75 Pa" is knowledge about the AS 2047 form, not
 * about audit trails.
 */
export function formatAuditFieldLabel(fieldName: string): string {
  const raw = fieldName.trim().replace(/_/g, ' ');
  if (!raw) return '';
  return splitIdentifier(raw).join(' ');
}

/**
 * Splits a PascalCase / camelCase / digit-bearing identifier into its words.
 *
 * The lookahead matters: a capital is only a new word when a lower-case letter follows it. Without
 * it `Rw_dB` reads as "Rw d B", because a trailing capital in these names is far more often a unit
 * (dB, Pa, kW) than the start of a word.
 */
function splitIdentifier(value: string): string[] {
  return value
    .replace(/([a-z0-9])([A-Z])(?=[a-z])/g, '$1 $2')
    .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
    .replace(/([0-9])([A-Z])/g, '$1 $2')
    .split(/\s+/)
    .filter((word) => word.length > 0);
}

/**
 * The words a transition is shown in, for the trail's "from -> to" line.
 *
 * The wire carries the state's TOKEN (`draft`, `underReview`), which is the storage name and
 * not what a reader is shown anywhere else: every badge resolves it through the state registry.
 * The trail printed the token verbatim, so a report page read "draft -> submitted" beneath a
 * badge saying "Submitted", and "underReview" appeared nowhere else in the product. A host that
 * names its state domain gets the registry's words; one that does not still gets the token, so a
 * trail with no domain to name is unchanged rather than blank.
 */
export function transitionWords(
  domain: StateDomainKey | undefined,
  token: string | null | undefined
): string {
  const raw = (token ?? '').trim();
  if (!raw || !domain) return raw;
  return resolveState(domain, raw).label;
}
